import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MAX_DOCUMENT_UPLOAD_BYTES, type CreateDocumentInput } from '@proref/schemas';
import type { DocumentType as DocType } from '@proref/types';
import { useAuth } from '../../contexts/auth-context';
import { useSportsQuery } from '../../hooks/use-sports';
import {
  useCreateDocumentUploadUrl,
  useUploadDocumentToS3,
} from '../../hooks/use-documents';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/shared/Modal';
import { Field } from '../../components/shared/Field';
import { Input } from '../../components/shared/Input';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { notifyError, notifySuccess } from '../../lib/toast';

interface FormValues {
  title: string;
  type: DocType;
  sport_id: string;
}

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
}

const MAX_MB = Math.floor(MAX_DOCUMENT_UPLOAD_BYTES / 1024 / 1024);
const DOC_TYPES: { value: DocType; label: string }[] = [
  { value: 'rulebook', label: 'Rulebook' },
  { value: 'commentary', label: 'Commentary' },
  { value: 'signal_code', label: 'Signal code' },
  { value: 'template', label: 'Template' },
];

// Derive the format from the picked file's extension — the API pins the matching content-type
// into the presigned PUT, so the two must agree. Only pdf/docx are accepted.
function formatFor(file: File): 'pdf' | 'docx' | null {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.docx')) return 'docx';
  return null;
}

const CONTENT_TYPE: Record<'pdf' | 'docx', string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

// "Upload a document" modal, opened from the Documents page (admins only). Runs the same two-step
// upload as clips (presign → S3 PUT). A super_admin uploads a PLATFORM doc and must pick the sport;
// a federation_admin's sport + federation are JWT-derived, so no picker is shown for them.
export function UploadDocumentDialog({ open, onClose }: UploadDocumentDialogProps) {
  const { role } = useAuth();
  const isSuper = role === 'super_admin';

  const sports = useSportsQuery(isSuper);
  const [file, setFile] = useState<File | null>(null);
  const createUploadUrl = useCreateDocumentUploadUrl();
  const uploadFile = useUploadDocumentToS3();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { title: '', type: 'rulebook', sport_id: '' } });

  const isBusy = createUploadUrl.isPending || uploadFile.isPending;

  function closeAndReset() {
    reset();
    setFile(null);
    onClose();
  }

  const onSubmit = handleSubmit(async (values) => {
    if (!file) {
      notifyError(new Error('Pick a file'), 'No file selected');
      return;
    }
    const fmt = formatFor(file);
    if (!fmt) {
      notifyError(new Error('Unsupported file'), 'Only PDF or DOCX files are allowed');
      return;
    }
    if (file.size > MAX_DOCUMENT_UPLOAD_BYTES) {
      notifyError(new Error('File too large'), `Files must be under ${MAX_MB} MB`);
      return;
    }

    try {
      const payload: CreateDocumentInput = {
        title: values.title,
        type: values.type,
        file_format: fmt,
        content_length: file.size,
        ...(isSuper && { sport_id: values.sport_id }),
      };
      const { document, upload_url } = await createUploadUrl.mutateAsync(payload);
      await uploadFile.mutateAsync({ url: upload_url, file, contentType: CONTENT_TYPE[fmt] });

      notifySuccess(`"${document.title}" uploaded`);
      void qc.invalidateQueries({ queryKey: ['documents'] });
      closeAndReset();
    } catch (err) {
      notifyError(err, 'Upload failed');
    }
  });

  return (
    <Modal
      open={open}
      onClose={isBusy ? () => {} : closeAndReset}
      title="Upload a document"
      sub="Add a rule-book, signal code or template. PDF or DOCX."
      footer={
        <>
          <Button onClick={closeAndReset} disabled={isBusy}>
            Cancel
          </Button>
          <Button form="upload-document-form" type="submit" variant="accent" disabled={isBusy || !file}>
            {isBusy ? (
              <>
                <Spinner className="w-3.5 h-3.5" />
                {uploadFile.isPending ? 'Uploading…' : 'Preparing…'}
              </>
            ) : (
              <>
                Upload <Icon.upload className="w-3.5 h-3.5" />
              </>
            )}
          </Button>
        </>
      }
    >
      <form id="upload-document-form" onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
        <Field label="Title" htmlFor="doc-title" error={errors.title?.message}>
          <Input
            id="doc-title"
            placeholder="IHF Rules of the Game 2025"
            {...register('title', { required: 'Title is required', maxLength: 200 })}
          />
        </Field>

        <Field label="Type" htmlFor="doc-type" error={errors.type?.message}>
          <select
            id="doc-type"
            className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
            {...register('type', { required: true })}
          >
            {DOC_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>

        {/* super_admin picks the sport for a platform doc; fed_admin's sport is JWT-derived. */}
        {isSuper && (
          <Field label="Sport" htmlFor="doc-sport" error={errors.sport_id?.message}>
            <select
              id="doc-sport"
              className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
              {...register('sport_id', { required: 'Pick a sport' })}
            >
              <option value="">Choose…</option>
              {(sports.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label={`File (PDF or DOCX, max ${MAX_MB} MB)`} htmlFor="doc-file">
          <input
            id="doc-file"
            type="file"
            accept=".pdf,.docx"
            onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-accent-tint file:px-3 file:py-2 file:text-[13px] file:font-medium file:text-accent hover:file:opacity-80"
          />
          {file && (
            <div className="mt-1 text-[11.5px] text-muted">
              {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
            </div>
          )}
        </Field>
      </form>
    </Modal>
  );
}
